const REDACTED_HEADERS = new Set(['authorization', 'x-lk-refresh-token', 'cookie', 'set-cookie']);

const sanitizeHeaders = (headers: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      REDACTED_HEADERS.has(key.toLowerCase()) ? '[REDACTED]' : value,
    ]),
  );

export const toHeaderRecord = (headers: unknown): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const normalizedHeaders: Record<string, string> = {};
    headers.forEach((value, key) => {
      normalizedHeaders[key] = value;
    });
    return sanitizeHeaders(normalizedHeaders);
  }

  if (typeof headers === 'object' && headers !== null) {
    const maybeHeaders = headers as {
      toJSON?: () => unknown;
      entries?: () => IterableIterator<[string, string]>;
    };

    if (typeof maybeHeaders.toJSON === 'function') {
      const normalizedHeaders = maybeHeaders.toJSON();
      if (normalizedHeaders && typeof normalizedHeaders === 'object') {
        return sanitizeHeaders(
          Object.fromEntries(
            Object.entries(normalizedHeaders as Record<string, unknown>).map(([key, value]) => [
              key,
              Array.isArray(value) ? value.map(String).join(', ') : String(value),
            ]),
          ),
        );
      }
    }

    if (typeof maybeHeaders.entries === 'function') {
      try {
        return sanitizeHeaders(Object.fromEntries(maybeHeaders.entries()));
      } catch {
        // Fall through to plain object handling.
      }
    }

    return sanitizeHeaders(
      Object.fromEntries(
        Object.entries(headers as Record<string, unknown>).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.map(String).join(', ') : String(value),
        ]),
      ),
    );
  }

  return {};
};

export const serializeForSentry = (value: unknown): unknown => {
  if (
    value == null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const serializedFormData: Record<string, unknown> = {};
    value.forEach((entryValue, key) => {
      serializedFormData[key] =
        typeof entryValue === 'string'
          ? entryValue
          : {
              name: entryValue.name,
              size: entryValue.size,
              type: entryValue.type,
            };
    });
    return serializedFormData;
  }

  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    const serializedSearchParams: Record<string, string> = {};
    value.forEach((entryValue, key) => {
      serializedSearchParams[key] = entryValue;
    });
    return serializedSearchParams;
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return {
      size: value.size,
      type: value.type,
    };
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return String(value);
  }
};
